import { addDays, format, isEqual } from 'date-fns';
// jl-design-system
import replaceStringVars from 'jl-design-system/utils/string/replaceStringVars';
// lodash
import get from 'lodash/get';
import has from 'lodash/has';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import sortBy from 'lodash/sortBy';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';

import {
  SET_COLLECTION_POINT_VIEW_TYPE,
  CLEAR_COLLECTION_POINT_SELECTION,
  FILTER_COLLECTION_POINTS,
  FIND_COLLECTION_POINT,
  LOGIN_RESET,
  SELECT_COLLECTION_POINT,
  SET_FIND_COLLECTION_POINT_ERROR,
  GET_COLLECTION_POINT_DETAILS,
  NEW_COLLECTION_POINT_SEARCH,
  HAND_OVER_RESET,
  PUT_CC_DELIVERY_DETAILS,
  PUT_CC_DELIVERY_DETAILS_SILENTLY,
  REDUX_FORM_SET_SUBMIT_FAILED,
  SET_ACTIVE_MAP_MARKER,
  SET_ACTIVE_MODAL_MAP_MARKER,
  ROUTER_LOCATION_CHANGE,
  GET_COLLECTION_POINT_DATES,
  SET_CLICK_COLLECT_COUNTDOWN_VISIBILITY,
  SET_CLICK_COLLECT_CUT_OFF_PASSED,
  SET_CLICK_COLLECT_CUT_OFF_PASSED_MODAL_VISIBILITY,
  SHOW_REMOVE_COLLECTION_POINT_OVERLAY,
  HIDE_REMOVE_COLLECTION_POINT_OVERLAY,
  DELETE_COLLECTION_POINT,
  POST_DELIVERY_PAGE,
  GET_DELIVERY_PAGE,
  SET_GEO_LOCATION_SEARCH_ACTIVE_STATUS,
  SET_COLLECT_PLUS_SELECTED_DATE,
  GET_PAYMENT_PAGE,
  INIT_PAYMENT_PAGE,
  GOOGLE_MAPS_SCRIPT_LOADED,
} from '../../../constants/actionConstants';
import {
  FILTER_COLLECT_PLUS,
  VIEW_TYPE_LIST,
  VIEW_TYPE_MAP,
  STORE_SELECTED,
  STORE_DETAILS,
  COLLECT_PLUS_OWNER_ID,
} from '../../../constants/clickCollectConstants';
import errorCodeConstants from '../../../constants/errorCodeConstants';
import { getNonGlobalError } from '../../../utils/error/parseError';
import getClickAndCollectStoreIdFromPath from '../../../utils/delivery/getClickAndCollectStoreIdFromPath';
import { isZeroAmount } from '../../../utils';
import getClickCollectFormattedTime from '../../../utils/helpers/time';
import getFilteredCollectionPoints from './getFilteredCollectionPoints';

export const INITIAL_STATE = {
  activeViewType: VIEW_TYPE_LIST,
  collectionPointSearchTerm: '',
  collectionPointSearchResults: [],
  collectionPointsOwners: [],
  totalCollectionPoints: 0,
  unfilteredCollectionPoints: [],
  getCollectionPointDetailsCallActive: false,
  collectionPointSearchActive: false,
  collectionPointSearchComplete: false,
  findCollectionPointError: undefined,
  filteredCollectionPoints: undefined,
  activeModalMapMarker: undefined,
  storeDetailsModalOpen: false,
  confirmedCollectionPoint: undefined,
  selectedCollectionPoint: undefined,
  hasNoLocalResultsNotification: undefined,
  collectPlusFilter: {},
  proceedingToPayment: false,
  activeMapMarker: undefined,
  confirmedCollectorDetails: undefined,
  isInitialSelection: undefined,
  selectedCollectionTime: undefined,
  countdownVisible: undefined,
  suggestedNewCollectionDate: {},
  cutOffTimeForSelectedStore: undefined,
  cutOffTimeForAllStores: undefined,
  showCutOffPassedModal: false,
  deleteCollectionPointApiCallActive: false,
  showCollectionPointDeleteSuccessMessage: false,
  showCollectionPointDeleteFailedMessage: false,
  collectionPointIdToBeRemoved: undefined,
  removeCollectionPointOverlayVisible: false,
  geoLocationSearchActive: false,
  savedCollectionPoints: [],
};

const getCollectionPointSearchFiltersActiveStatus = (cpPreviouslyActive, jlCollectionPoints, cpCollectionPoints) => {
  if (cpCollectionPoints.length) {
    if (cpPreviouslyActive || !jlCollectionPoints.length) {
      return true;
    }
  }

  return false;
};

const getCollectionPointSearchFiltersConfig = (collectionPoints, previousFilter) => {
  const jlCollectionPoints = collectionPoints.filter(point => point.ownerId !== COLLECT_PLUS_OWNER_ID);
  const cpCollectionPoints = collectionPoints.filter(point => point.ownerId === COLLECT_PLUS_OWNER_ID);

  const filtersActiveStatus = getCollectionPointSearchFiltersActiveStatus(
    previousFilter.active,
    jlCollectionPoints,
    cpCollectionPoints,
  );

  return {
    active: filtersActiveStatus,
    id: FILTER_COLLECT_PLUS,
    label: 'Show local Collect+ shops',
    priceTimeLabel: cpCollectionPoints.length ? 'Collect ${time} for ${cost}' : 'No shops available',
    hidden: isEmpty(jlCollectionPoints) || isEmpty(cpCollectionPoints),
  };
};

export function getSelectCollectionPointButtonText(point) {
  return point.isHeadOffice ? 'Select office' : 'Select shop';
}

export const mapSavedCollectionPoints = points => (
  points.map(point => ({
    ...point,
    isSaved: true,
    selectCollectionPointButtonText: getSelectCollectionPointButtonText(point),
  }))
);

function formatCollectionPoints(collectionPoints) {
  if (collectionPoints) {
    const jlwPoints = filter(collectionPoints, ({ ownerId }) => (ownerId !== COLLECT_PLUS_OWNER_ID));
    const cpPoints = filter(collectionPoints, ({ ownerId }) => (ownerId === COLLECT_PLUS_OWNER_ID));
    const jlwPointsSorted = sortBy(jlwPoints, ['collectionCharge']);
    const cpPointsSorted = sortBy(cpPoints, ['collectionCharge']);

    const points = collectionPoints.map((item = {}) => ({
      ...item,
      selectCollectionPointButtonText: getSelectCollectionPointButtonText(item),
    }));

    return {
      cpPoints,
      cpPointsSorted,
      jlwPoints,
      jlwPointsSorted,
      points,
    };
  }
  return {};
}

function findStoreById(state, id) {
  return find([
    ...[...state.unfilteredCollectionPoints, ...state.savedCollectionPoints],
    state.selectedCollectionPoint,
  ], { id });
}

export default function clickAndCollectReducer(state = INITIAL_STATE, action = {}) {
  switch (action.type) {
    case HAND_OVER_RESET: {
      return {
        ...INITIAL_STATE,
      };
    }

    case '@@redux-form/CHANGE':
    case SET_COLLECT_PLUS_SELECTED_DATE: {
      const field = get(action, 'meta.field');

      if (field === 'collectionDateTime' || action.type === SET_COLLECT_PLUS_SELECTED_DATE) {
        const selectedDateIndex = findIndex(state.collectionPointDates, item => item.date === action.payload);
        const selectedDate = state.collectionPointDates[selectedDateIndex];
        const selectedDateFormatted = format(get(selectedDate, 'date', ''), 'YYYY-MM-DD');
        const nextDayFormatted = format(addDays(new Date(), 1), 'YYYY-MM-DD');
        const nextDayIsSelected = isEqual(selectedDateFormatted, nextDayFormatted);
        const availableDates = state.collectionPointDates.filter(({ isAvailable }) => isAvailable);

        const suggestedNewCollectionDate = nextDayIsSelected ?
          availableDates[1] : {};

        return {
          ...state,
          selectedCollectionDate: selectedDate,
          cutOffTimeForSelectedStore: get(selectedDate, 'cutOffTime', null),
          selectedCollectionTime: action.payload,
          focusOnSelectedDayMessage: true,
          suggestedNewCollectionDate,
        };
      }

      return state;
    }

    case `${GET_COLLECTION_POINT_DATES}.LOADING`: {
      return {
        ...state,
        selectedCollectionTime: undefined,
        collectionPointDates: undefined,
        getCollectionPointDatesFailed: false,
        getCollectionPointDatesApiCallActive: true,
        collectionDateTimeString: undefined,
        selectingCollectionPointId: action.selectingCollectionPointId,
      };
    }

    case `${GET_COLLECTION_POINT_DATES}.FAILED`: {
      return {
        ...state,
        collectionPointDates: undefined,
        selectedCollectionTime: undefined,
        getCollectionPointDatesFailed: true,
        getCollectionPointDatesApiCallActive: false,
        collectionDateTimeString: undefined,
        selectingCollectionPointId: undefined,
      };
    }

    case `${GET_COLLECTION_POINT_DATES}.SUCCESS`: {
      const cutOffTime = get(find(action.result.collectionDates, date => has(date, 'cutOffTime')), 'cutOffTime');
      const collectionDateTimeString = get(find(action.result.collectionDates,
        date => has(date, 'collectionDateTimeString')), 'collectionDateTimeString');

      return {
        ...state,
        collectionPointDates: action.result.collectionDates,
        cutOffTimeForSelectedStore: cutOffTime,
        getCollectionPointDatesFailed: false,
        getCollectionPointDatesApiCallActive: false,
        collectionDateTimeString,
        selectingCollectionPointId: undefined,
      };
    }

    case `${GET_COLLECTION_POINT_DETAILS}.LOADING`:
      return {
        ...state,
        getCollectionPointDetailsCallActive: true,
      };

    case `${GET_COLLECTION_POINT_DETAILS}.FAILED`: {
      return {
        ...state,
        getCollectionPointDetailsCallActive: false,
        collectionPointServiceDown: true,
      };
    }

    // used when C&C store is selected and partner discount is toggled on/off
    // used when edit basket could have caused a change in collection point charges
    case `${GET_COLLECTION_POINT_DETAILS}.SUCCESS`: {
      const collectionPoint = get(action, 'result.collectionPoints[0]');
      const collectionPoints = state.collectionPointSearchResults;

      if (collectionPoint) {

        const collectionPointIndex = findIndex(collectionPoints, {
          dtsfcLocationId: collectionPoint.dtsfcLocationId,
        });

        const updatedCollectionPoint = {
          ...collectionPoints[collectionPointIndex],
          ...collectionPoint,
          isDefault: get(state, 'selectedCollectionPoint.isDefault', false),
        };

        const unfilteredCollectionPoints = get(state, 'unfilteredCollectionPoints', []);
        const collectionPointExists = find(unfilteredCollectionPoints, {
          dtsfcLocationId: collectionPoint.dtsfcLocationId,
        });

        if (!collectionPointExists) {
          unfilteredCollectionPoints.push(updatedCollectionPoint);
        }

        return {
          ...state,
          getCollectionPointDetailsCallActive: false,
          unfilteredCollectionPoints,
          selectedCollectionPoint: updatedCollectionPoint,
        };
      }

      return {
        ...state,
        getCollectionPointDetailsCallActive: false,
      };
    }

    case `${FIND_COLLECTION_POINT}.LOADING`:
      return {
        ...state,
        collectionPointSearchActive: !action.refresh,
      };

    case `${FIND_COLLECTION_POINT}.FAILED`:
      if (get(action, 'error.code') === errorCodeConstants.INCOMPLETE_COLLECTION_SEARCH) {
        return {
          ...state,
          collectionPointSearchResults: [],
          hasNoLocalResultsNotification: undefined,
          collectionPointSearchComplete: false,
          collectionPointSearchActive: false,
          findCollectionPointError: getNonGlobalError(
            {
              error: {
                code: errorCodeConstants.INCOMPLETE_COLLECTION_SEARCH,
              },
            },
            state.findCollectionPointError,
          ),
        };
      }

      return {
        ...state,
        collectionPointSearchResults: [],
        hasNoLocalResultsNotification: undefined,
        collectionPointSearchComplete: false,
        collectionPointSearchActive: false,
        collectionPointServiceDown: true,
      };

    case `${FIND_COLLECTION_POINT}.SUCCESS`: {
      const collectionPointSearchTerm = get(action, 'collectionPointSearchTerm', '');
      const totalCollectionPoints = get(action, 'result.totalFound', 0);
      const cutOffTimeForAllStores = get(action, 'result.cutOffTime');
      const collectionPointsOwners = get(action, 'result.collectionPointsOwners');
      const alwaysShowCollectPlusFeatureActive = get(action, 'alwaysShowCollectPlusFeatureActive');

      const {
        points,
        jlwPointsSorted,
        cpPointsSorted,
      } = formatCollectionPoints(get(action, 'result.collectionPoints', []));

      const hasLocalResults = get(action, 'result.hasLocalResults', true);

      const switchToListTab = points.length === 1 && !!points[0].closedToThePublic;
      const activeModalMapMarker = state.modalStoreId ? find(points, { id: state.modalStoreId }) : undefined;
      const storeDetailsModalOpen = !!activeModalMapMarker;
      const collectPlusFilterConfig = getCollectionPointSearchFiltersConfig(
        points,
        state.collectPlusFilter,
      );

      const collectionPointSearchResults = getFilteredCollectionPoints(
        points,
        collectPlusFilterConfig,
        alwaysShowCollectPlusFeatureActive,
      );

      return {
        ...state,
        activeViewType: switchToListTab ? VIEW_TYPE_LIST : state.activeViewType,
        collectionPointSearchActive: false,
        collectionPointSearchComplete: totalCollectionPoints !== 0,
        selectedCollectionPoint: action.refresh ? state.selectedCollectionPoint : undefined,
        collectionPointSearchTerm,
        unfilteredCollectionPoints: points,
        collectionPointSearchResults,
        collectionPointsOwners,
        activeMapMarker: collectionPointSearchResults[0],
        totalCollectionPoints,
        hasNoLocalResultsNotification: hasLocalResults ? undefined : getNonGlobalError(
          { error: { code: errorCodeConstants.CLIENT_NO_LOCAL_COLLECTION_POINTS_FOUND } },
          state.hasNoLocalResultsNotification,
        ),

        findCollectionPointError: totalCollectionPoints === 0 ? getNonGlobalError(
          { error: { code: errorCodeConstants.CLIENT_NO_COLLECTION_POINT_FOUND_ERROR } },
          state.findCollectionPointError,
        ) : undefined,
        collectPlusFilter: {
          ...collectPlusFilterConfig,
          priceTimeLabel: replaceStringVars(collectPlusFilterConfig.priceTimeLabel, {
            cost: get(cpPointsSorted, '[0].collectionChargeFormatted'),
            time: getClickCollectFormattedTime({
              time: get(cpPointsSorted, '[0].collectionTime'),
              bold: false,
              dayOnly: true,
            }),
          }),
        },
        isFreeCollection: isZeroAmount(get(jlwPointsSorted, '[0].collectionCharge', 0)),
        activeModalMapMarker,
        storeDetailsModalOpen,
        cutOffTimeForAllStores,
        earliestCollectionDateTime: get(action, 'result.earliestCollectionDateTimeString'),
      };
    }

    case `${PUT_CC_DELIVERY_DETAILS}.LOADING`:
      return {
        ...state,
        proceedingToPayment: true,
      };

    case `${PUT_CC_DELIVERY_DETAILS}.FAILED`: {
      const errorCode = get(action, 'error.code');

      return {
        ...state,
        proceedingToPayment: false,
        collectionPointServiceDown: errorCode !== errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE,
      };
    }

    case `${PUT_CC_DELIVERY_DETAILS}.SUCCESS`: {
      const fulfilmentOfferId = get(action, 'body.fulfilment.fulfilmentOfferId');
      const selectedDate = find(state.collectionPointDates, item => item.fulfilmentOfferId === fulfilmentOfferId);

      return {
        ...state,
        cutOffTimeForSelectedStore: get(selectedDate, 'cutOffTime', null),
        selectedCollectionTime: get(selectedDate, 'date', ''),
        proceedingToPayment: true,
        confirmedCollectionPoint: action.collectionPoint,
        confirmedCollectorDetails: action.body,
        isInitialSelection: false,
        showCutOffPassedModal: false,
        cutOffPassed: false,
        countdownVisible: false,
        // for re-use in payment page ClickAndCollectCutOffPassedModal scenario
        collectorDetails: get(action, 'body.collectorDetails'),
        giftMessageLines: get(action, 'body.fulfilment.giftMessageLines'),
        cutOffPassedDuringOrderSubmit: false,
      };
    }

    case `${PUT_CC_DELIVERY_DETAILS_SILENTLY}.FAILED`: {
      return {
        ...state,
        cutOffPassedDuringOrderSubmit: true,
      };
    }

    case `${GET_PAYMENT_PAGE}.FAILED`:
    case `${INIT_PAYMENT_PAGE}.FAILED`:
    case `${GET_PAYMENT_PAGE}.SUCCESS`:
    case `${INIT_PAYMENT_PAGE}.SUCCESS`:
      return {
        ...state,
        proceedingToPayment: false,
      };

    case CLEAR_COLLECTION_POINT_SELECTION:
    case NEW_COLLECTION_POINT_SEARCH:
      return {
        ...state,
        selectedCollectionPoint: undefined,
        hasNoLocalResultsNotification: undefined,
      };

    case SELECT_COLLECTION_POINT: {
      const sharedState = {
        selectedCollectionPoint: action.collectionPoint,
        isInitialSelection: state.isInitialSelection === undefined,
        collectionPointSearchComplete: true,
      };

      if (get(action, 'collectionPoint.ownerId') === COLLECT_PLUS_OWNER_ID) {
        return {
          ...state,
          ...sharedState,
          selectedCollectionTime: undefined,
          cutOffTimeForSelectedStore: undefined,
          countdownVisible: false,
        };
      }

      return {
        ...state,
        ...sharedState,
      };
    }

    case SET_FIND_COLLECTION_POINT_ERROR: {
      return {
        ...state,
        findCollectionPointError: getNonGlobalError(action, state.findCollectionPointError),
      };
    }

    case FILTER_COLLECTION_POINTS: {

      const collectPlusFilter = {
        ...state.collectPlusFilter,
        active: !state.collectPlusFilter.active,
      };

      const filteredCollectionPoints = getFilteredCollectionPoints(
        state.unfilteredCollectionPoints,
        collectPlusFilter,
      );

      return {
        ...state,
        collectPlusFilter,
        collectionPointSearchResults: filteredCollectionPoints,
      };
    }

    case SET_COLLECTION_POINT_VIEW_TYPE:
      return {
        ...state,
        activeViewType: action.activeViewType,
      };

    case SET_ACTIVE_MAP_MARKER:
      return {
        ...state,
        activeMapMarker: action.marker,
      };

    case SET_ACTIVE_MODAL_MAP_MARKER:
      return {
        ...state,
        activeModalMapMarker: action.marker,
      };

    case LOGIN_RESET:
      return {
        ...INITIAL_STATE,
      };

    case ROUTER_LOCATION_CHANGE: {
      const pathname = get(action, 'payload.location.pathname', '');
      const modalStoreId = getClickAndCollectStoreIdFromPath({ path: pathname });
      const isStoreDetailsRoute = includes(pathname, STORE_DETAILS);

      if (isStoreDetailsRoute) {
        const modalShowSelectShopButton = !state.selectedCollectionPoint ||
          state.selectedCollectionPoint.id !== modalStoreId;

        return {
          ...state,
          activeModalMapMarker: findStoreById(state, modalStoreId),
          storeDetailsModalOpen: true,
          modalShowSelectShopButton,
        };
      }

      const pathNameParams = pathname.split('/');
      const storeSearchResultsRoute = includes([VIEW_TYPE_LIST, VIEW_TYPE_MAP], pathNameParams[2]);
      const selectStoreRoute = pathNameParams[2] === STORE_SELECTED;

      let selectedCollectionPoint = state.selectedCollectionPoint;
      const shouldReSelectCollectionPoint = selectStoreRoute && !selectedCollectionPoint;

      if (shouldReSelectCollectionPoint) {
        // customer has navigated forward through a store-select route
        selectedCollectionPoint = findStoreById(state, pathNameParams[3]);
      } else if (storeSearchResultsRoute) {
        // customer has navigated back to before they selected a collection point
        selectedCollectionPoint = undefined;
      }

      return {
        ...state,
        activeViewType: storeSearchResultsRoute ? pathNameParams[2] : state.activeViewType,
        selectedCollectionPoint,
        modalStoreId,
        storeDetailsModalOpen: false,
        showCollectionPointDeleteSuccessMessage: false,
        removeCollectionPointOverlayVisible: false,
      };
    }

    case '@@redux-form/UPDATE_SYNC_ERRORS':
      return {
        ...state,
        findCollectionPointError: undefined,
      };

    case REDUX_FORM_SET_SUBMIT_FAILED:
      return {
        ...state,
        findCollectionPointError: undefined,
      };

    case SET_CLICK_COLLECT_COUNTDOWN_VISIBILITY:

      if (action.visible && !state.countdownHasBeenVisible) {
        return {
          ...state,
          countdownVisible: action.visible,
          countdownHasBeenVisible: true,
        };
      }

      return {
        ...state,
        countdownVisible: action.visible,
      };

    case SET_CLICK_COLLECT_CUT_OFF_PASSED_MODAL_VISIBILITY:
      return {
        ...state,
        showCutOffPassedModal: action.visible,
      };

    case SET_CLICK_COLLECT_CUT_OFF_PASSED:
      return {
        ...state,
        cutOffPassed: action.cutOffPassed,
      };

    case `${DELETE_COLLECTION_POINT}.LOADING`:
      return {
        ...state,
        deleteCollectionPointApiCallActive: true,
        showCollectionPointDeleteFailedMessage: false,
        showCollectionPointDeleteSuccessMessage: false,
      };

    case `${DELETE_COLLECTION_POINT}.FAILED`:
      return {
        ...state,
        deleteCollectionPointApiCallActive: false,
        showCollectionPointDeleteFailedMessage: true,
        collectionPointIdToBeRemoved: undefined,
        removeCollectionPointOverlayVisible: false,
      };

    case `${DELETE_COLLECTION_POINT}.SUCCESS`:
      return {
        ...state,
        deleteCollectionPointApiCallActive: false,
        showCollectionPointDeleteSuccessMessage: true,
        collectionPointIdToBeRemoved: undefined,
        removeCollectionPointOverlayVisible: false,
      };

    case SHOW_REMOVE_COLLECTION_POINT_OVERLAY:
      return {
        ...state,
        collectionPointIdToBeRemoved: action.id,
        removeCollectionPointOverlayVisible: true,
      };

    case HIDE_REMOVE_COLLECTION_POINT_OVERLAY:
      return {
        ...state,
        collectionPointIdToBeRemoved: undefined,
        removeCollectionPointOverlayVisible: false,
      };

    case SET_GEO_LOCATION_SEARCH_ACTIVE_STATUS:
      return {
        ...state,
        geoLocationSearchActive: action.active,
      };

    case `${POST_DELIVERY_PAGE}.SUCCESS`:
    case `${GET_DELIVERY_PAGE}.SUCCESS`:
      return {
        ...state,
        focusOnSelectedDayMessage: false,
      };

    case GOOGLE_MAPS_SCRIPT_LOADED:
      return {
        ...state,
        googleMapsScriptLoaded: true,
      };

    default:
      return state;
  }
}
